import {put, select, takeLatest, race, take, all, call} from 'redux-saga/effects';
import {SagaIterator} from 'redux-saga';
import {CATEGORY, createGAEvent, HP, pushDataLayer} from '@optimaxdev/analytics/mobile';
import {FULFILLED, REJECTED} from 'redux-promise-middleware';

import {getIsCustomerLoaded, isCustomerLogged} from 'selectors/customer/customer';
import {fetchReorderItem, startWaitAuthResult} from 'features/reorder/reducers/reorder/reorder';
import {isContactLensesPage, isHomePage} from 'selectors/router/router';
import {CUSTOMER, LOCATION_CHANGED, MENU} from 'constants/actions';
import {
    isReorderAvailable as isReorderAvailableSelector,
    getReorderToken,
    getCustomerStatus,
} from 'features/reorder/selectors/reorder';
import {getUrlParameter} from 'libs/url';
import {push} from 'connected-react-router';
import {addToCartReorderCL} from 'actions/cart/cart';
import {isContactLenses} from 'selectors/product/product';

/**
 * Checks is user logged in and if not, then does not fetch reorder banner
 */
export function* gerReorderItemForLoggedUser(): SagaIterator {
    const [isUserOnHomePage, isClCategoryPage, isCustomerLoaded, isContactsPDP] = yield all([
        select(isHomePage),
        select(isContactLensesPage),
        select(getIsCustomerLoaded),
        select(isContactLenses),
    ]);

    if (!isCustomerLoaded) return;

    if (isClCategoryPage || isUserOnHomePage || isContactsPDP) {
        yield put(fetchReorderItem(isUserOnHomePage));
    }
}

/**
 * It redirects to cart after login from banner on category page if user has CL in previous order
 */
export function* redirectToCartAfterLoginOnCategory() {
    const isUserLoggedInFromPopup = yield call(getUrlParameter, 'reorder');
    const [isClCategoryPage, reorderToken] = yield all([
        select(isContactLensesPage),
        select(getReorderToken),
    ]);
    if (isUserLoggedInFromPopup && isClCategoryPage) {
        yield put(addToCartReorderCL(reorderToken));
        yield put(push('/checkout/cart'));
    }
}

/**
 * it sends analytics when customer wants to log in from reorder popup
 */
export function* sendAnalytics() {
    const isReorderAvailable = yield select(isReorderAvailableSelector);

    pushDataLayer({
        ...createGAEvent(
            CATEGORY.CONTACT_LENSES_REORDER_POPUP,
            {
                version: 'M',
                label: 'Is first CL order',
            },
            false,
        ),
        eventValue: Number(!isReorderAvailable),
    });
}

/**
 *  this need for analytics sagas below due resolve types errors
 * @param {string} name - name of event
 * @param {string} label - event label
 * @param {boolean} isInteraction - isInteraction
 */
const pushDataLayerAnalytics = (name, label, isInteraction = false) => {
    pushDataLayer(
        createGAEvent(
            name,
            {
                version: 'M',
                label,
            },
            isInteraction,
        ),
    );
};

/**
 * it sends analytics for returned user from Home page
 */
export function* sendRecognizedUserAnalyticsFromHomePage() {
    const customerNameStatus = yield select(getCustomerStatus);

    pushDataLayerAnalytics(HP.HP, `Display - ${customerNameStatus}`, false);
}

/**
 * it sends analytics for returned user from CL category page
 */
export function* sendRecognizedUserAnalyticsFromCLCategory() {
    const customerNameStatus = yield select(getCustomerStatus);

    pushDataLayerAnalytics(
        CATEGORY.CONTACT_LENSES_BANNER,
        `Display - ${customerNameStatus}`,
        false,
    );
}

/**
 * it calls sending analytics for recognized user
 */
export function* sendRecognizedUserAnalytics() {
    const isCL = yield select(isContactLensesPage);
    const isHP = yield select(isHomePage);
    const isReorderAvailable = yield select(isReorderAvailableSelector);

    if (isCL && isReorderAvailable) yield call(sendRecognizedUserAnalyticsFromCLCategory);
    if (isHP && isReorderAvailable) yield call(sendRecognizedUserAnalyticsFromHomePage);
}

/**
 * It calls when customer had success auth
 * it's needed only in one case, when customer clicks SIgn in from category popup
 */
export function* waitAuthResult() {
    yield race([
        take(`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`),
        take(`${MENU.SET_VISIBILITY_MENU}`),
    ]);

    const isUserLoggedIn = yield select(isCustomerLogged);
    if (isUserLoggedIn) {
        yield race([
            take(`${fetchReorderItem}_${FULFILLED}`),
            take(`${fetchReorderItem}_${REJECTED}`),
        ]);
        yield call(sendAnalytics);
    }
}

/**
 * Root
 */
export function* reorderSaga() {
    yield takeLatest(
        [`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`, LOCATION_CHANGED],
        gerReorderItemForLoggedUser,
    );
    yield takeLatest(`${startWaitAuthResult}`, waitAuthResult);
    yield takeLatest([`${fetchReorderItem}_${FULFILLED}`], redirectToCartAfterLoginOnCategory);
    yield takeLatest(`${fetchReorderItem}_${FULFILLED}`, sendRecognizedUserAnalytics);
}
